import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import "./ContractMonitor.css";

// ✅ Import AdminNeonCard from: src/components/AdminNeonCard.jsx
// ContractMonitor.jsx is in: src/components/Dashboard/AdminDashboard/AllUsers/
import AdminNeonCard from "../../../AdminNeonCard";

/**
 * ADMIN — CONTRACT MONITOR
 * Endpoints:
 *  - GET /api/admin/contracts/active
 *  - GET /api/admin/contracts/expiring?days=7
 *  - GET /api/admin/contracts/terminated
 *  - GET /api/admin/contracts/completed
 *
 * Notes:
 * - This monitor is ONLY for paid/activated contracts.
 * - Terminated contracts appear only in the terminated feed, not active.
 * - Completed = Expired + Terminated (completed lifecycle)
 */

const DEFAULT_EXPIRING_DAYS = 7;

// ✅ Backend base (dev/prod safe)
// - If you have REACT_APP_API_ORIGIN set (e.g. https://api.yourdomain.com), it will use that.
// - Otherwise defaults to localhost:5000 for dev.
const BACKEND_ORIGIN = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");

const parseDurationMs = (durationStr) => {
    const lower = String(durationStr || "").toLowerCase().trim();
    const number = parseFloat(lower);
    if (!Number.isFinite(number)) return 0;

    if (lower.includes("year")) return number * 365 * 24 * 60 * 60 * 1000;
    if (lower.includes("month")) return number * 30 * 24 * 60 * 60 * 1000;
    if (lower.includes("day")) return number * 24 * 60 * 60 * 1000;
    if (lower.includes("hour")) return number * 60 * 60 * 1000;
    if (lower.includes("minute")) return number * 60 * 1000;
    return 0;
};

const formatShort = (ms) => {
    if (!Number.isFinite(ms)) return "—";
    if (ms <= 0) return "Expired";

    const totalSeconds = Math.floor(ms / 1000);

    const days = Math.floor(totalSeconds / (60 * 60 * 24));
    const hours = Math.floor((totalSeconds / (60 * 60)) % 24);
    const minutes = Math.floor((totalSeconds / 60) % 60);

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
};

const getActivationStartMs = (c) => {
    const t =
        c?.activatedAt ||
        c?.paidAt ||
        c?.paymentPaidAt ||
        c?.paymentCompletedAt ||
        c?.paymentSuccessTime ||
        c?.activationTime ||
        null;

    if (!t) return null;
    const ms = new Date(t).getTime();
    return Number.isNaN(ms) ? null : ms;
};

const computeTimeLeftMs = (contract) => {
    const startMs = getActivationStartMs(contract);
    if (!startMs) return null;

    const durationMs = parseDurationMs(contract?.contractDuration);
    if (!durationMs) return null;

    const endMs = startMs + durationMs;
    return endMs - Date.now();
};

const cleanName = (v) => {
    const s = String(v ?? "").trim();
    return s.length ? s : null;
};

const resolvePlayerProfileSlug = (raw) => {
    // backend is now sending playerProfileSlug; keep safe fallbacks
    return cleanName(raw?.playerProfileSlug) || cleanName(raw?.PlayerDatum?.profileSlug) || null;
};

const resolvePartnerProfileSlug = (raw) => {
    // backend is now sending partnerProfileSlug; keep safe fallbacks
    return (
        cleanName(raw?.partnerProfileSlug) ||
        cleanName(raw?.Agent?.profileSlug) ||
        cleanName(raw?.Scout?.profileSlug) ||
        cleanName(raw?.Club?.profileSlug) ||
        null
    );
};

// ✅ Make contract URLs work in dev (3000) when uploads are served from backend (5000)
const resolveContractUrl = (url) => {
    if (!url) return null;
    const s = String(url).trim();
    if (!s) return null;

    // If it's already an absolute URL, keep it (but fix localhost:3000/uploads -> backend origin)
    if (/^https?:\/\//i.test(s)) {
        try {
            const u = new URL(s);
            if (
                (u.hostname === "localhost" || u.hostname === "127.0.0.1") &&
                u.port === "3000" &&
                u.pathname.startsWith("/uploads/")
            ) {
                return `${BACKEND_ORIGIN}${u.pathname}${u.search || ""}${u.hash || ""}`;
            }
            return s;
        } catch {
            return s;
        }
    }

    // If it's a relative uploads path, assume backend serves it
    if (s.startsWith("/uploads/")) {
        return `${BACKEND_ORIGIN}${s}`;
    }

    return s;
};

// ✅ Decide how to label the card based on tab + completion type
const getUiTitleAndBadge = (raw, tab, timeLeftMs) => {
    const completionType = String(raw?.completionType || "").toLowerCase().trim(); // backend completed feed
    const status = String(raw?.status || "").toLowerCase().trim();

    // Active + Expiring are still active contracts
    if (tab === "active") return { uiContractTitle: "ACTIVE CONTRACT", badgeTitle: "LIVE" };
    if (tab === "expiring") return { uiContractTitle: "ACTIVE CONTRACT", badgeTitle: "EXPIRING" };

    if (tab === "terminated") return { uiContractTitle: "TERMINATED CONTRACT", badgeTitle: "TERMINATED" };

    if (tab === "completed") {
        // Prefer backend completionType
        if (completionType === "terminated") {
            return { uiContractTitle: "TERMINATED CONTRACT", badgeTitle: "COMPLETED" };
        }
        if (completionType === "expired") {
            return { uiContractTitle: "EXPIRED CONTRACT", badgeTitle: "COMPLETED" };
        }

        // Fallback inference
        if (status === "terminated") return { uiContractTitle: "TERMINATED CONTRACT", badgeTitle: "COMPLETED" };
        if (timeLeftMs !== null && Number.isFinite(timeLeftMs) && timeLeftMs <= 0) {
            return { uiContractTitle: "EXPIRED CONTRACT", badgeTitle: "COMPLETED" };
        }

        // Safe fallback
        return { uiContractTitle: "COMPLETED CONTRACT", badgeTitle: "COMPLETED" };
    }

    return { uiContractTitle: "ACTIVE CONTRACT", badgeTitle: "LIVE" };
};

const normalizeContractForCard = (raw, tab) => {
    // compute time left
    const timeLeftMs = computeTimeLeftMs(raw);

    const signedAt = raw?.signedOn || raw?.submittedAt || raw?.createdAt || null;

    const scoutName = cleanName(raw?.scoutName);
    const agentName = cleanName(raw?.agentName);
    const clubName = cleanName(raw?.clubName);

    const playerProfileSlug = resolvePlayerProfileSlug(raw);
    const partnerProfileSlug = resolvePartnerProfileSlug(raw);

    const { uiContractTitle, badgeTitle } = getUiTitleAndBadge(raw, tab, timeLeftMs);

    // ✅ Status line: keep your raw status, but make it match tab expectations
    // - terminated feed should show "terminated"
    // - completed expired should show "expired"
    // - active/expiring should show raw status or "active"
    const rawStatus = String(raw?.status || "active");
    const completionType = String(raw?.completionType || "").toLowerCase().trim();

    let statusText = rawStatus;
    if (tab === "terminated") statusText = "terminated";
    if (tab === "completed") {
        if (completionType === "expired") statusText = "expired";
        else if (completionType === "terminated") statusText = "terminated";
        else if (rawStatus.toLowerCase() === "terminated") statusText = "terminated";
        else if (timeLeftMs !== null && Number.isFinite(timeLeftMs) && timeLeftMs <= 0) statusText = "expired";
        else statusText = rawStatus;
    }

    return {
        id: raw?.id,

        // parties
        scoutName: scoutName || "—",
        playerName: cleanName(raw?.playerName || raw?.playerFullName) || "—",
        playerUsername: cleanName(raw?.playerUsername) || "—",

        // optional parties
        agentName,
        clubName,

        // slugs for profile links
        playerProfileSlug,
        partnerProfileSlug,

        // contract url
        agentContractUrl: raw?.agentContractUrl || null,

        // contract info
        contractDuration: cleanName(raw?.contractDuration) || "—",
        timeLeft: timeLeftMs === null ? "—" : formatShort(timeLeftMs),
        signedAt: signedAt ? new Date(signedAt).toISOString() : null,

        // ✅ NEW: Card title control (used by updated AdminNeonCard)
        uiContractTitle,

        // status
        statusText: statusText.toString(),
        badge: { subtitle: "Status", title: badgeTitle },

        // keep the full object for actions / modal
        _raw: raw,
    };
};

const ContractMonitor = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [activeContracts, setActiveContracts] = useState([]);
    const [expiringSoon, setExpiringSoon] = useState([]);
    const [terminatedContracts, setTerminatedContracts] = useState([]);
    const [completedContracts, setCompletedContracts] = useState([]);

    const [tab, setTab] = useState("active"); // active | expiring | terminated | completed
    const [expiringDays] = useState(DEFAULT_EXPIRING_DAYS);

    // ✅ “LIVE” heartbeat (tiny dot pulse)
    const [beat, setBeat] = useState(false);

    // ✅ View modal state
    const [selected, setSelected] = useState(null);

    // Fetch all feeds
    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError("");

            try {
                const [activeRes, expRes, termRes, compRes] = await Promise.all([
                    apiFetch(`/api/admin/contracts/active`, { method: "GET" }),
                    apiFetch(`/api/admin/contracts/expiring?days=${encodeURIComponent(expiringDays)}`, {
                        method: "GET",
                    }),
                    apiFetch(`/api/admin/contracts/terminated`, { method: "GET" }),
                    apiFetch(`/api/admin/contracts/completed`, { method: "GET" }),
                ]);

                const readJson = async (res) => {
                    const txt = await res.text().catch(() => "");
                    try {
                        return txt ? JSON.parse(txt) : [];
                    } catch {
                        return [];
                    }
                };

                if (!activeRes.ok) throw new Error("Failed to fetch active contracts.");
                if (!expRes.ok) throw new Error("Failed to fetch expiring contracts.");
                if (!termRes.ok) throw new Error("Failed to fetch terminated contracts.");
                if (!compRes.ok) throw new Error("Failed to fetch completed contracts.");

                const [activeData, expData, termData, compData] = await Promise.all([
                    readJson(activeRes),
                    readJson(expRes),
                    readJson(termRes),
                    readJson(compRes),
                ]);

                if (cancelled) return;

                setActiveContracts(Array.isArray(activeData) ? activeData : []);
                setExpiringSoon(Array.isArray(expData) ? expData : []);
                setTerminatedContracts(Array.isArray(termData) ? termData : []);
                setCompletedContracts(Array.isArray(compData) ? compData : []);
            } catch (e) {
                if (cancelled) return;
                setError(e?.message || "Failed to load contract monitor data.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        // refresh every 20s (data)
        const id = setInterval(load, 20000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [expiringDays]);

    // “live” heartbeat
    useEffect(() => {
        const id = setInterval(() => setBeat((b) => !b), 900);
        return () => clearInterval(id);
    }, []);

    // Update time-left every minute (lightweight)
    useEffect(() => {
        const id = setInterval(() => {
            setActiveContracts((prev) => [...prev]);
            setExpiringSoon((prev) => [...prev]);
        }, 60000);
        return () => clearInterval(id);
    }, []);

    // ESC to close modal
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setSelected(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const stats = useMemo(() => {
        return {
            active: activeContracts.length,
            expiring: expiringSoon.length,
            terminated: terminatedContracts.length,
            completed: completedContracts.length,
        };
    }, [
        activeContracts.length,
        expiringSoon.length,
        terminatedContracts.length,
        completedContracts.length,
    ]);

    const currentFeed = useMemo(() => {
        if (tab === "expiring") return expiringSoon;
        if (tab === "terminated") return terminatedContracts;
        if (tab === "completed") return completedContracts;
        return activeContracts;
    }, [tab, activeContracts, expiringSoon, terminatedContracts, completedContracts]);

    // ✅ Pass tab into normalization so AdminNeonCard can show the right title
    const cards = useMemo(() => currentFeed.map((raw) => normalizeContractForCard(raw, tab)), [currentFeed, tab]);

    const openInNewTab = (url) => {
        const finalUrl = resolveContractUrl(url);
        if (!finalUrl) return;
        window.open(finalUrl, "_blank", "noopener,noreferrer");
    };

    const goTo = (url) => {
        if (!url) return;
        window.location.href = url;
    };

    return (
        <div className="contract-monitor">
            <div className="contract-monitor-header">
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <h1 style={{ margin: 0 }}>Contract Monitoring Center</h1>
                    <span
                        title="Live feed"
                        style={{
                            width: 10,
                            height: 10,
                            borderRadius: 999,
                            background: beat ? "#39ff14" : "#1a3b14",
                            boxShadow: beat ? "0 0 10px rgba(57,255,20,0.8)" : "none",
                            display: "inline-block",
                        }}
                    />
                </div>
                <p>Live overview of activated contracts</p>
            </div>

            <div className="contract-stats">
                <div
                    className={`stat-card ${tab === "active" ? "selected" : ""}`}
                    onClick={() => setTab("active")}
                    style={{ cursor: "pointer" }}
                    title="Show active contracts"
                >
                    <h3>Active Contracts</h3>
                    <span>{stats.active}</span>
                </div>

                <div
                    className={`stat-card warning ${tab === "expiring" ? "selected" : ""}`}
                    onClick={() => setTab("expiring")}
                    style={{ cursor: "pointer" }}
                    title={`Show contracts expiring within ${expiringDays} days`}
                >
                    <h3>Expiring Soon</h3>
                    <span>{stats.expiring}</span>
                </div>

                <div
                    className={`stat-card danger ${tab === "terminated" ? "selected" : ""}`}
                    onClick={() => setTab("terminated")}
                    style={{ cursor: "pointer" }}
                    title="Show terminated contracts"
                >
                    <h3>Terminated</h3>
                    <span>{stats.terminated}</span>
                </div>

                <div
                    className={`stat-card ${tab === "completed" ? "selected" : ""}`}
                    onClick={() => setTab("completed")}
                    style={{ cursor: "pointer" }}
                    title="Show completed contracts (expired + terminated)"
                >
                    <h3>Completed</h3>
                    <span>{stats.completed}</span>
                </div>
            </div>

            {loading ? (
                <p style={{ color: "#bbb" }}>Loading...</p>
            ) : error ? (
                <p style={{ color: "salmon" }}>{error}</p>
            ) : cards.length === 0 ? (
                <p style={{ color: "#bbb" }}>
                    {tab === "active"
                        ? "No active contracts yet."
                        : tab === "expiring"
                            ? "No contracts expiring soon."
                            : tab === "terminated"
                                ? "No terminated contracts."
                                : "No completed contracts."}
                </p>
            ) : (
                <div className="contracts-grid">
                    {cards.map((c) => {
                        const playerSlug = c?.playerProfileSlug;
                        const partnerSlug = c?.partnerProfileSlug;

                        const playerProfileHref = playerSlug ? `/player-profile/${playerSlug}` : null;
                        const partnerRole = String(c?._raw?.signedByRole || "").toLowerCase();

                        const partnerProfileHref =
                            partnerSlug && partnerRole === "scout"
                                ? `/scout-profile/${partnerSlug}`
                                : partnerSlug && partnerRole === "agent"
                                    ? `/agent-profile/${partnerSlug}`
                                    : partnerSlug && partnerRole === "club"
                                        ? `/club-profile/${partnerSlug}`
                                        : null;

                        return (
                            <AdminNeonCard key={c.id || `${c.playerUsername}-${c.signedAt}`} contract={c}>
                                <div className="card-actions">
                                    <button className="btn view" onClick={() => setSelected(c._raw)}>
                                        View
                                    </button>

                                    {c?.agentContractUrl ? (
                                        <button
                                            type="button"
                                            className="btn view"
                                            onClick={() => openInNewTab(c.agentContractUrl)}
                                            title="Open uploaded contract"
                                        >
                                            View Contract
                                        </button>
                                    ) : null}

                                    {playerProfileHref ? (
                                        <button
                                            type="button"
                                            className="btn view"
                                            onClick={() => goTo(playerProfileHref)}
                                            title="Open player profile"
                                        >
                                            Player Profile
                                        </button>
                                    ) : null}

                                    {partnerProfileHref ? (
                                        <button
                                            type="button"
                                            className="btn view"
                                            onClick={() => goTo(partnerProfileHref)}
                                            title="Open partner profile"
                                        >
                                            Partner Profile
                                        </button>
                                    ) : null}
                                </div>
                            </AdminNeonCard>
                        );
                    })}
                </div>
            )}

            {selected ? <ContractDetailsModal contract={selected} onClose={() => setSelected(null)} /> : null}
        </div>
    );
};

export default ContractMonitor;

function ContractDetailsModal({ contract, onClose }) {
    if (!contract) return null;

    const contractId = contract?.id ?? "—";

    const fmt = (d) => {
        if (!d) return "—";
        const t = new Date(d);
        return Number.isNaN(t.getTime()) ? "—" : t.toLocaleString();
    };

    const paidAt = fmt(contract?.paidAt);
    const submittedAt = fmt(contract?.submittedAt);
    const adminApprovalTime = fmt(contract?.adminApprovalTime);
    const activationHold = fmt(contract?.activationHoldExpiresAt);
    const expiresAt = contract?.expiresAt ? fmt(contract.expiresAt) : "—";

    const copyId = async () => {
        try {
            await navigator.clipboard.writeText(String(contractId));
        } catch {
            // ignore clipboard failures
        }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.72)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: "min(980px, 96vw)",
                    maxHeight: "90vh",
                    overflow: "auto",
                    background: "#070707",
                    border: "1px solid rgba(57,255,20,0.35)",
                    boxShadow: "0 0 24px rgba(57,255,20,0.18)",
                    borderRadius: 14,
                    padding: 18,
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div>
                        <div style={{ color: "#39ff14", fontWeight: 800, letterSpacing: 1 }}>
                            CONTRACT DETAILS
                        </div>

                        <div
                            style={{
                                marginTop: 8,
                                display: "flex",
                                gap: 10,
                                flexWrap: "wrap",
                                alignItems: "center",
                            }}
                        >
                            <span style={{ color: "#bbb" }}>Contract ID:</span>
                            <span style={{ color: "#fff" }}>{contractId}</span>
                            <button
                                onClick={copyId}
                                style={{
                                    background: "transparent",
                                    border: "1px solid rgba(57,255,20,0.35)",
                                    color: "#39ff14",
                                    padding: "4px 10px",
                                    borderRadius: 10,
                                    cursor: "pointer",
                                }}
                                title="Copy Contract ID"
                            >
                                Copy
                            </button>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        style={{
                            background: "transparent",
                            border: "1px solid rgba(255,255,255,0.15)",
                            color: "#fff",
                            padding: "6px 12px",
                            borderRadius: 10,
                            cursor: "pointer",
                            height: 34,
                        }}
                    >
                        Close
                    </button>
                </div>

                <div
                    style={{
                        marginTop: 16,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 14,
                    }}
                >
                    <DetailRow label="Signed By Role" value={contract?.signedByRole ?? "—"} />
                    <DetailRow label="Status" value={contract?.status ?? "—"} />

                    <DetailRow label="Admin Approval" value={contract?.adminApproval ?? "—"} />
                    <DetailRow label="Admin Approval Time" value={adminApprovalTime} />

                    <DetailRow label="Signed By Player" value={String(!!contract?.signedByPlayer)} />
                    <DetailRow label="Payment Status" value={contract?.paymentStatus ?? "—"} />

                    <DetailRow label="Paid At" value={paidAt} />
                    <DetailRow label="Expires At" value={expiresAt} />

                    <DetailRow label="Activation Hold Expires" value={activationHold} />
                    <DetailRow label="Submitted At" value={submittedAt} />

                    <DetailRow label="Contract Duration" value={contract?.contractDuration ?? "—"} />
                    <DetailRow label="Agree To Terms" value={String(!!contract?.agreeToTerms)} />

                    <DetailRow label="Termination Reason" value={contract?.terminationReason ?? "—"} />
                    <DetailRow label="Rejection Reason" value={contract?.rejectionReason ?? "—"} />

                    <DetailRow label="Withdraw Reason" value={contract?.withdrawReason ?? "—"} />
                    <DetailRow label="Player Note" value={contract?.playerNote ?? "—"} />

                    <DetailRow label="Stripe Session ID" value={contract?.stripeSessionId ?? "—"} />
                    <DetailRow label="Payment Intent ID" value={contract?.paymentIntentId ?? "—"} />

                    <DetailRow label="Refund ID" value={contract?.refundId ?? "—"} />
                    <DetailRow label="Refunded At" value={fmt(contract?.refundedAt)} />

                    <DetailRow label="Created At" value={fmt(contract?.createdAt)} />
                    <DetailRow label="Updated At" value={fmt(contract?.updatedAt)} />

                    <DetailRow label="IP Address" value={contract?.ipAddress ?? "—"} />
                    <DetailRow label="Notes" value={contract?.notes ?? "—"} />
                </div>
            </div>
        </div>
    );
}

function DetailRow({ label, value }) {
    const v = value === null || value === undefined || String(value).trim() === "" ? "—" : value;

    return (
        <div
            style={{
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 12,
                padding: 12,
                background: "rgba(255,255,255,0.02)",
            }}
        >
            <div style={{ color: "#bbb", fontSize: 12 }}>{label}</div>
            <div style={{ color: "#fff", marginTop: 4, wordBreak: "break-word" }}>{v}</div>
        </div>
    );
}